import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Link2, FolderTree, Shield, BarChart3, Megaphone, Settings, UserCog, Key, Mail, ShoppingBag, Plug, Scale, Search } from "lucide-react";
import Header from "./Header";

const navGroups = [
  {
    label: "WiLink commercial",
    testId: "nav-group-commercial",
    items: [
      { to: "/admin", end: true, label: "Vue d'ensemble", icon: LayoutDashboard, testId: "nav-dashboard" },
      { to: "/admin/licenses", label: "Licences", icon: Key, testId: "nav-licenses" },
      { to: "/admin/contact", label: "Boîte contact", icon: Mail, testId: "nav-contact" },
      { to: "/admin/commerce", label: "Tarifs & paiements", icon: ShoppingBag, testId: "nav-commerce" },
      { to: "/admin/integrations", label: "Intégrations", icon: Plug, testId: "nav-integrations" },
      { to: "/admin/legal", label: "Pages légales", icon: Scale, testId: "nav-legal" },
      { to: "/admin/seo", label: "SEO", icon: Search, testId: "nav-seo" },
    ],
  },
  {
    label: "Portail démo",
    testId: "nav-group-demo",
    items: [
      { to: "/admin/links", label: "Liens", icon: Link2, testId: "nav-links" },
      { to: "/admin/categories", label: "Catégories", icon: FolderTree, testId: "nav-categories" },
      { to: "/admin/announcements", label: "Annonces", icon: Megaphone, testId: "nav-announcements" },
      { to: "/admin/whitelist", label: "Whitelist IP", icon: Shield, testId: "nav-whitelist" },
      { to: "/admin/stats", label: "Statistiques", icon: BarChart3, testId: "nav-stats" },
      { to: "/admin/settings", label: "Réglages portail", icon: Settings, testId: "nav-settings" },
    ],
  },
  {
    label: "Compte",
    testId: "nav-group-account",
    items: [
      { to: "/admin/profile", label: "Mon profil", icon: UserCog, testId: "nav-profile" },
    ],
  },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Backoffice · WiLink";
  }, []);

  React.useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/admin/login", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Chargement…</p>
      </div>
    );
  }

  const renderItem = (item) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        data-testid={item.testId}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${
            isActive
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-accent"
          }`
        }
      >
        <Icon className="h-4 w-4" />
        {item.label}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header siteTitle="WiLink Backoffice" />
      <div className="flex-1 flex flex-col md:flex-row max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 gap-6 py-6">
        <aside className="md:w-60 shrink-0" data-testid="admin-sidebar">
          {/* Mobile : tout sur une ligne, sans labels de groupe */}
          <nav className="md:hidden flex gap-1 overflow-x-auto">
            {navGroups.flatMap((g) => g.items).map(renderItem)}
          </nav>

          {/* Desktop : groupes séparés visuellement */}
          <nav className="hidden md:flex md:flex-col gap-1">
            {navGroups.map((group, idx) => (
              <div key={group.label} data-testid={group.testId} className={idx > 0 ? "mt-5" : ""}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-bold px-3 mb-1.5">
                  {group.label}
                </p>
                <div className="flex flex-col gap-1">
                  {group.items.map(renderItem)}
                </div>
              </div>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
